import {
    ArgoMaterialQuantityKindEnum,
    GetAllSerialNumberFormatsForMaterialClassDocument,
    GetMaterialClassDocument,
    type Maybe,
} from '@redviking/argonaut-core-ui/types/db';
import { accessor } from '../store';
import { Route } from 'vue-router';
import { RouteEntityDetail } from '@redviking/argonaut-core-ui/src/components/EntityDetail';
import { entityDetailRoute } from 'src/components/EntityDetail/entity-route-util';
import { messages } from 'src/i18n/i18n';
import { gqlClient } from '@redviking/argonaut-core-ui/src/util/gql-client';
import { Notify } from 'src/notifications';
import {
    type MaterialClassData,
    type MaterialClassEntity,
    saveMaterialClassEntity,
} from 'src/material-class/material-class.entity';
import { validateSerialNumberFmtSelect } from 'src/material-class/serial-numbers/serial-number.validations';
import { validateMaterialSelectAcl, validateMaterialUpdateAcl } from 'src/material/material.validations';

export default [
    {

        name: 'materialClassList',
        path: 'class',
        component: () => import(/* webpackChunkName: "material" */ '../material-class/MaterialClassView.vue'),
        meta: {
            transition: 'horizontal',
            navbarIcon: '$argo-material-class',
            pageBannerTitle: 'Material Config',
            pageBannerSubtitle: 'View/ Edit Material Classes',
            navbarTitle: messages.material.material.materialDef,
        },
        children: [],

    },

    entityDetailRoute<'materialclass'>({
        path: 'class/:materialClassId',
        name: 'materialClassMaintenence',
        component: RouteEntityDetail,
        props: {
            open: true,
        },
        redirect: to => ({ name: 'materialClassSettings', params: to.params }),
        meta: {
            returnRoute: (route: Route) => ({ name: 'materialClassList', query: route.query }),
            canEdit: validateMaterialUpdateAcl,
            entityType: 'materialclass',
            getEntity: async (to) => {
                if (to.query.mode === 'create') {
                    const entityData: MaterialClassEntity = {
                        ignoreWarnings: false,
                        pendingClassLinkedMedia: [],
                        pendingModelLinkedMedia: {},
                        pendingProfilePictureMedia: null,
                        serialNumberFormatsRelatedToMaterialConfig: [],
                        materialClass: {
                            material_quantity_type: ArgoMaterialQuantityKindEnum.Distinct,
                            linked_media: [],
                            serialNumberFormatId: null,
                            linked_profile_picture: null,
                            id: to.params.materialClassId,
                            description: '',
                            name: 'Unnamed Material Class',
                            context_id: accessor.auth.contextId,
                            created_by: accessor.auth.currentUser!,
                            material_models: [],
                            material_class_processes: [],
                            udf_schemas: [],
                            material_class_attribute_settings: [],
                            meta: {
                                udfDefaultData: {},
                            },
                        },
                    };
                    return {
                        entity: entityData,
                    };
                }

                const { materialClass } = await gqlClient.request({
                    document: GetMaterialClassDocument,
                    variables: {
                        materialClassId: to.params.materialClassId,
                    },
                });
                if (materialClass) {
                    const entityData: MaterialClassData = {
                        id: materialClass.id,
                        name: materialClass.name,
                        meta: materialClass.meta,
                        context_id: materialClass.context_id,
                        created_at: materialClass.created_at,
                        created_by: materialClass.created_by,
                        udf_schemas: materialClass.udf_schemas,
                        description: materialClass.description,
                        linked_media: materialClass.linked_media,
                        serialNumberFormatId: materialClass.sn_format?.id || null,
                        linked_profile_picture: materialClass.linked_profile_picture,
                        material_class_processes: materialClass.material_class_processes,
                        material_class_attribute_settings: materialClass.material_class_attribute_settings,
                        material_quantity_type: materialClass.material_quantity_type as Maybe<ArgoMaterialQuantityKindEnum> || ArgoMaterialQuantityKindEnum.Distinct,
                        material_models: materialClass.material_models.map(mm => ({
                            new: false,
                            modelCfg: {
                                ...mm,
                                serialNumberFormatId: mm.sn_format?.id || null,
                            },
                        })),
                    };
                    const {
                        argo_sn_format,
                    } = await gqlClient.request({
                        document: GetAllSerialNumberFormatsForMaterialClassDocument,
                        variables: {
                            materialClassId: to.params.materialClassId,
                        },
                    });
                    const materialEntity: MaterialClassEntity = {
                        ignoreWarnings: false,
                        materialClass: entityData,
                        pendingClassLinkedMedia: [],
                        pendingModelLinkedMedia: {},
                        pendingProfilePictureMedia: null,
                        serialNumberFormatsRelatedToMaterialConfig: argo_sn_format.map(snFormat => ({
                            new: false,
                            id: snFormat.id,
                            name: snFormat.name,
                            config: snFormat.config,
                            status: snFormat.status,
                            revision: snFormat.revision,
                            validation: snFormat.validation,
                            description: snFormat.description,
                        })),
                    };
                    return {
                        entity: materialEntity,
                    };
                } else {
                    const err = new Error('Unknown Material Class');
                    Notify.error(err);
                    throw err;
                }


            },
            saveEntity: saveMaterialClassEntity,
            pageBannerTitle: () => {
                const materialClass = accessor.entityAsType('materialclass');
                return materialClass ? materialClass.materialClass.name : messages.materialClass.titles.noName;
            },
            pageBannerSubtitle: () => messages.entityNames.materialclass,
        },
        children: [
            {
                path: 'settings',
                name: 'materialClassSettings',
                component: () => import(/* webpackChunkName: "material" */ '../material-class/MaterialClassSettings.view.vue'), // a component for the settings page content
                meta: {
                    tab: { // the presence of this prop indicates this is a tab
                        label: 'Settings',
                        icon: 'mdi-cog',
                    },
                    navigationGuard: validateMaterialSelectAcl,
                },
            },
            {
                path: 'material-class-serial-number-list',
                name: 'materialClassSerialNumberList',
                component: () => import(/* webpackChunkName: "material" */ './MaterialClassSerialNumberEditor.vue'),
                meta: {
                    tab: { // the presence of this prop indicates this is a tab
                        label: 'Serial Number Formats',
                        icon: 'mdi-numeric',
                    },
                    navigationGuard: validateSerialNumberFmtSelect,
                },
            },
            {
                path: 'material-class-process-list',
                name: 'materialClassProcessList',
                component: () => import(/* webpackChunkName: "material" */ '../material-class/MaterialClassProcesses.view.vue'), // a component for the settings page content
                meta: {
                    tab: { // the presence of this prop indicates this is a tab
                        label: 'Processes',
                        icon: '$argo-process',
                    },
                    navigationGuard: () => validateMaterialSelectAcl() && accessor.featureFlags.processes,
                },
            },
            {
                path: 'material-class-file-list',
                name: 'materialClassFileList',
                component: () => import(/* webpackChunkName: "material" */ '../material-class/MaterialClassFileManager.vue'), // a component for the settings page content
                meta: {
                    tab: { // the presence of this prop indicates this is a tab
                        label: 'Files',
                        icon: 'mdi-file-document-multiple-outline',
                    },
                    navigationGuard: validateMaterialSelectAcl,
                },
            },
            {
                path: 'material-class-attribute-settings',
                name: 'materialClassAttributeSettings',
                component: () => import(/* webpackChunkName: "material" */ '../material-class/MaterialClassAttributeSettings.view.vue'), // a component for the settings page content
                meta: {
                    tab: { // the presence of this prop indicates this is a tab
                        label: 'Attribute Settings',
                        icon: 'mdi-animation-outline',
                    },
                    navigationGuard: () => validateMaterialSelectAcl() && accessor.featureFlags.attributes,
                },
            },
        ],
    }),
];
